import { invokeApiRaw } from '@/lib/apiClient';

import { useState, useRef, useCallback, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface UseAudioRecorderReturn {
  isRecording: boolean;
  isTranscribing: boolean;
  duration: number;
  startRecording: () => Promise<void>;
  stopRecording: () => Promise<string>;
  cancelRecording: () => void;
  error: string | null;
}

const MAX_DURATION_SECONDS = 60;

type SpeechRecognitionLike = {
  lang: string;
  interimResults: boolean;
  continuous: boolean;
  onresult: ((event: any) => void) | null;
  onerror: ((event: any) => void) | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
  abort: () => void;
};

function normalizeMimeType(mimeType: string): string {
  return (mimeType || 'audio/webm').split(';')[0].trim() || 'audio/webm';
}

export function useAudioRecorder(): UseAudioRecorderReturn {
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const resolveRef = useRef<((text: string) => void) | null>(null);
  const speechRecognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const speechTranscriptRef = useRef('');
  const activeModeRef = useRef<'speech' | 'media' | null>(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const stopMediaRecorder = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  }, []);

  const transcribeAudio = useCallback(async (audioBlob: Blob): Promise<string> => {
    const mimeType = normalizeMimeType(audioBlob.type || 'audio/webm');
    
    // Convert blob to base64
    const base64 = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const dataUrl = reader.result as string;
        // Extract base64 part after the comma
        const base64Data = dataUrl.split(',')[1];
        resolve(base64Data);
      };
      reader.onerror = reject;
      reader.readAsDataURL(audioBlob);
    });

    const { data, error } = await invokeApiRaw<{ text?: string; error?: string }>('transcribe-audio', {
      body: { audioBase64: base64, mimeType },
    });

    if (error) {
      throw new Error(error.message || 'Error al transcribir el audio');
    }

    if (data?.error) {
      throw new Error(data.error);
    }

    return data?.text || '';
  }, []);

  const stopSpeechRecognition = useCallback(() => {
    try {
      speechRecognitionRef.current?.stop();
    } catch {
      // ignore
    }
    speechRecognitionRef.current = null;
  }, []);

  const startRecording = useCallback(async () => {
    setError(null);
    chunksRef.current = [];
    setDuration(0);
    speechTranscriptRef.current = '';

    try {
      const SpeechRecognitionCtor =
        typeof window !== 'undefined'
          ? ((window as any).SpeechRecognition || (window as any).webkitSpeechRecognition)
          : null;

      if (SpeechRecognitionCtor) {
        activeModeRef.current = 'speech';
        const recognition = new SpeechRecognitionCtor() as SpeechRecognitionLike;
        speechRecognitionRef.current = recognition;

        recognition.lang = 'es-ES';
        recognition.interimResults = true;
        recognition.continuous = true;

        recognition.onresult = (event) => {
          let finalText = '';
          for (let i = event.resultIndex; i < event.results.length; i += 1) {
            const result = event.results[i];
            const transcript = result?.[0]?.transcript || '';
            if (result.isFinal) {
              finalText += `${transcript} `;
            }
          }
          if (finalText.trim()) {
            speechTranscriptRef.current = `${speechTranscriptRef.current} ${finalText}`.trim();
          }
        };

        recognition.onerror = (event) => {
          const message = event?.error || 'Error en reconocimiento de voz';
          setError(message);
          setIsRecording(false);
          setIsTranscribing(false);
          stopTimer();
          resolveRef.current?.(speechTranscriptRef.current.trim() || '[Audio no reconocido]');
          resolveRef.current = null;
          speechRecognitionRef.current = null;
        };

        recognition.onend = () => {
          stopTimer();
          setIsRecording(false);
          setIsTranscribing(false);
          const normalizedText = speechTranscriptRef.current.trim();
          resolveRef.current?.(normalizedText || '[Audio no reconocido]');
          resolveRef.current = null;
          speechRecognitionRef.current = null;
        };

        recognition.start();
        setIsRecording(true);

        timerRef.current = setInterval(() => {
          setDuration(prev => {
            const next = prev + 1;
            if (next >= MAX_DURATION_SECONDS) {
              stopSpeechRecognition();
            }
            return next;
          });
        }, 1000);

        return;
      }

      activeModeRef.current = 'media';
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      // Try webm/opus first, fallback to webm, then any available
      let mimeType = 'audio/webm;codecs=opus';
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = 'audio/webm';
        if (!MediaRecorder.isTypeSupported(mimeType)) {
          mimeType = '';
        }
      }

      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      recorder.onstop = async () => {
        stopTimer();
        const audioBlob = new Blob(chunksRef.current, { type: recorder.mimeType });
        
        if (audioBlob.size === 0) {
          setIsRecording(false);
          setError('No se capturó audio');
          resolveRef.current?.('[Audio no reconocido]');
          resolveRef.current = null;
          return;
        }

        setIsRecording(false);
        setIsTranscribing(true);

        try {
          const text = await transcribeAudio(audioBlob);
          const normalizedText = text.trim();
          if (!normalizedText) {
            setError('No se pudo transcribir el audio');
            resolveRef.current?.('[Audio no reconocido]');
          } else {
            resolveRef.current?.(normalizedText);
          }
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Error al transcribir';
          setError(message);
          resolveRef.current?.('[Audio no reconocido]');
        } finally {
          resolveRef.current = null;
          setIsTranscribing(false);
        }
      };

      recorder.start(250); // collect chunks every 250ms
      setIsRecording(true);

      // Start duration timer
      timerRef.current = setInterval(() => {
        setDuration(prev => {
          const next = prev + 1;
          if (next >= MAX_DURATION_SECONDS) {
            stopMediaRecorder();
          }
          return next;
        });
      }, 1000);

    } catch (err) {
      const message = err instanceof Error ? err.message : 'Error al acceder al micrófono';
      if (message.includes('Permission') || message.includes('NotAllowed')) {
        setError('Permiso de micrófono denegado. Habilítalo en la configuración del navegador.');
      } else {
        setError(message);
      }
      throw err;
    }
  }, [stopSpeechRecognition, stopTimer, stopMediaRecorder, transcribeAudio]);

  const stopRecording = useCallback((): Promise<string> => {
    if (activeModeRef.current === 'speech') {
      return new Promise((resolve) => {
        resolveRef.current = resolve;
        stopSpeechRecognition();
      });
    }

    return new Promise((resolve) => {
      resolveRef.current = resolve;
      stopMediaRecorder();
    });
  }, [stopMediaRecorder, stopSpeechRecognition]);

  const cancelRecording = useCallback(() => {
    resolveRef.current = null;
    stopTimer();
    stopSpeechRecognition();
    stopMediaRecorder();
    chunksRef.current = [];
    setIsRecording(false);
    setIsTranscribing(false);
    setDuration(0);
    activeModeRef.current = null;
    speechTranscriptRef.current = '';
  }, [stopSpeechRecognition, stopTimer, stopMediaRecorder]);

  return {
    isRecording,
    isTranscribing,
    duration,
    startRecording,
    stopRecording,
    cancelRecording,
    error,
  };
}
